import React, { useEffect, useMemo, useState } from "react";
import { createUser, deleteUser, getUsers, updateUser } from "../../../api/users";
import { useNotification } from "../../../context/NotificationContext";
import { useAuth } from "../../../context/AuthContext";
import { DashboardReloadButton } from "./dashboardUi";

const ROLES = ["Admin", "Editor"];

const UsersManager = () => {
  const { notify } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    role: "Editor",
  });
  const [drafts, setDrafts] = useState({});

  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";
  const currentUserId = Number(user?.id);
  const hasAuthenticatedUserId = Number.isFinite(currentUserId) && currentUserId > 0;

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      const list = Array.isArray(data) ? data : [];
      setUsers(list);
      setDrafts(
        list.reduce((acc, item) => {
          acc[item.id] = {
            role: item.role || "Editor",
            isActive: Boolean(item.isActive),
            password: "",
          };
          return acc;
        }, {})
      );
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load users",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadUsers();
  }, [isSuperAdmin]);

  const canEditUser = (target) => {
    if (!target) return false;
    const targetRole = String(target.role || "").toLowerCase();
    if (targetRole === "superadmin") return false;
    return true;
  };

  const canDeleteUser = (target) => {
    if (!target) return false;
    const targetRole = String(target.role || "").toLowerCase();
    if (targetRole === "superadmin") return false;
    if (Number(target.id) === currentUserId) return false;
    return true;
  };

  const handleCreate = async () => {
    if (!hasAuthenticatedUserId) {
      notify({ type: "error", title: "Please login again", message: "Authenticated user id is missing in session." });
      return;
    }
    const username = createForm.username.trim().toLowerCase();
    if (!username) {
      notify({ type: "error", title: "Username is required" });
      return;
    }
    if ((createForm.password || "").length < 8) {
      notify({ type: "error", title: "Password must be at least 8 characters" });
      return;
    }

    setCreating(true);
    try {
      await createUser({
        username,
        password: createForm.password,
        role: createForm.role,
      });
      notify({ type: "success", title: "User created" });
      setCreateForm({ username: "", password: "", role: "Editor" });
      await loadUsers();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => Number(a.id || 0) - Number(b.id || 0)),
    [users]
  );

  if (!isSuperAdmin) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <p className="font-semibold text-slate-900">No access</p>
        <p className="mt-1 text-sm text-slate-500">Only SuperAdmin can manage users.</p>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadUsers} loading={loading} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        <p className="text-sm font-semibold text-[#0b1f3f] mb-3">Create user</p>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            type="text"
            value={createForm.username}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, username: e.target.value }))}
            placeholder="Username"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="password"
            value={createForm.password}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, password: e.target.value }))}
            placeholder="Password (min 8)"
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <select
            value={createForm.role}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, role: e.target.value }))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || !hasAuthenticatedUserId}
            className="rounded-xl bg-[#FE7E3F] font-medium text-white px-3 py-2 text-sm hover:bg-[#e86f32] disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create user"}
          </button>
        </div>
        {!hasAuthenticatedUserId ? (
          <p className="text-xs text-rose-600 mt-2">
            Authenticated user id is missing. Please logout/login again before editing users.
          </p>
        ) : null}
      </div>

      <div className="dashboard-table-wrap dashboard-h-scroll-light overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-[12%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="w-[24%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Username</th>
              <th className="w-[18%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
              <th className="w-[14%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Active</th>
              <th className="w-[20%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">New Password</th>
              <th className="w-[12%] px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  Loading users...
                </td>
              </tr>
            ) : sortedUsers.length ? (
              sortedUsers.map((u) => {
                const draft = drafts[u.id] || { role: u.role || "Editor", isActive: Boolean(u.isActive), password: "" };
                const editable = canEditUser(u);
                const deletable = canDeleteUser(u);
                return (
                  <tr key={u.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{u.id}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{u.username}</td>
                    <td className="px-3 py-3">
                      <select
                        disabled={!editable}
                        value={draft.role}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [u.id]: { ...draft, role: e.target.value },
                          }))
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          disabled={!editable || Number(u.id) === currentUserId}
                          checked={Boolean(draft.isActive)}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [u.id]: { ...draft, isActive: e.target.checked },
                            }))
                          }
                        />
                        Active
                      </label>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="password"
                        disabled={!editable}
                        value={draft.password}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [u.id]: { ...draft, password: e.target.value },
                          }))
                        }
                        placeholder="Optional"
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          disabled={!editable || !hasAuthenticatedUserId}
                          onClick={async () => {
                            if (!hasAuthenticatedUserId) {
                              notify({ type: "error", title: "Please login again", message: "Authenticated user id is missing in session." });
                              return;
                            }
                            if (draft.password && draft.password.length < 8) {
                              notify({ type: "error", title: "Password must be at least 8 characters" });
                              return;
                            }
                            try {
                              await updateUser({
                                id: u.id,
                                role: draft.role,
                                isActive: draft.isActive,
                                password: draft.password || undefined,
                                authenticatedUserId: currentUserId,
                              });
                              notify({ type: "success", title: "User updated" });
                              await loadUsers();
                            } catch (error) {
                              notify({
                                type: "error",
                                title: "Update failed",
                                message: error?.response?.data?.message || error?.message || "",
                              });
                            }
                          }}
                          className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10 disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          disabled={!deletable || !hasAuthenticatedUserId}
                          onClick={async () => {
                            if (!hasAuthenticatedUserId) {
                              notify({ type: "error", title: "Please login again", message: "Authenticated user id is missing in session." });
                              return;
                            }
                            const ok = window.confirm(`Delete user "${u.username}"?`);
                            if (!ok) return;
                            try {
                              await deleteUser({ id: u.id });
                              notify({ type: "success", title: "User deleted" });
                              await loadUsers();
                            } catch (error) {
                              notify({
                                type: "error",
                                title: "Delete failed",
                                message: error?.response?.data?.message || error?.message || "",
                              });
                            }
                          }}
                          className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default UsersManager;
